import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createVendorPayment,
  uploadVendorPaymentFile,
  getVendorPaymentHistory,
  downloadVendorPaymentFile,
} from "../../services/api/boq/purchaseService";
import { getVendors } from "../../services/api/boq/VendorService";
import { useMemo } from "react";
import { getPurchaseOrderById } from "../../services/api/boq/purchaseService";

export default function PurchaseOrderPayments() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [po, setPo] = useState(null);

  const [form, setForm] = useState({
    amount: "",
    payment_date: "",
    mode: "upi",
    txn_ref_no: "",
  });

  // 🔥 FETCH HISTORY
  const fetchHistory = async () => {
    try {
      const res = await getVendorPaymentHistory(id);
      setHistory(res.data || []);
    } catch (err) {
      toast.error("Failed to load history");
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      const [vendorsRes, poRes, historyRes] = await Promise.all([
        getVendors(),
        getPurchaseOrderById(id),
        getVendorPaymentHistory(id),
      ]);

      setVendors(vendorsRes?.data?.data?.data || []);
      setPo(poRes?.data?.data || poRes?.data || null);
      setHistory(historyRes?.data || []);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const vendorMap = useMemo(() => {
    const map = {};
    vendors.forEach((v) => {
      map[v.id] = v.name;
    });
    return map;
  }, [vendors]);

  const vendorName = useMemo(() => {
    if (!po) return "-";
    return vendorMap[po.vendor_id] || "Vendor";
  }, [po, vendorMap]);

  // 🔥 TOTAL PAID
  const totalPaid = history.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const balance = po?.total_amount ? po.total_amount - totalPaid : 0;

  const getStatus = () => {
    if (totalPaid === 0) return "Pending";
    if (totalPaid < po?.total_amount) return "Partial";
    return "Completed";
  };

  // 🔥 FORM CHANGE
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // 🔥 FILE PREVIEW
  const handleFile = (e) => {
    const f = e.target.files[0];
    setFile(f);

    if (f) {
      setPreview(URL.createObjectURL(f));
    }
  };

  // 🔥 SUBMIT PAYMENT
  const handleSubmit = async (e) => {
    e.preventDefault();

    const amount = Number(form.amount);

    // ❌ validation
    if (amount > balance) {
      toast.error(`Cannot pay more than balance ₹${balance}`);
      return;
    }

    try {
      setLoading(true);

      const res = await createVendorPayment({
        ...form,
        po_id: id,
        vendor_id: po?.vendor_id,
      });

      const paymentId = res.data.id;

      if (file) {
        await uploadVendorPaymentFile(paymentId, file);
      }

      toast.success("Payment added successfully");

      setForm({
        amount: "",
        payment_date: "",
        mode: "upi",
        txn_ref_no: "",
      });

      setFile(null);
      setPreview(null);

      fetchHistory();
    } catch (err) {
      toast.error("Payment failed");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 DOWNLOAD
  const handleDownload = async (paymentId) => {
    try {
      const blob = await downloadVendorPaymentFile(paymentId);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `payment_${paymentId}.pdf`;
      a.click();
    } catch {
      toast.error("Download failed");
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500">
        Loading Purchase Order details...
      </div>
    );
  }
  return (
    <div className="p-4 md:p-6 space-y-6">
      <button
        onClick={() => navigate("/purchase-orders")}
        className="text-blue-600 hover:text-blue-800"
      >
        &larr; Back to Orders
      </button>
      <div className="text-sm text-gray-600">
        Purchase Order ID: <b>{po?.po_number}</b>
        <br />
        Vendor: <b>{vendorName}</b>
      </div>

      {/* SUMMARY CARD */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="p-4 bg-white shadow rounded">
          <p>Total PO</p>
          <h2 className="text-xl font-bold">₹{po?.total_amount || 0}</h2>
        </div>

        <div className="p-4 bg-white shadow rounded">
          <p>Paid</p>
          <h2 className="text-xl font-bold text-green-600">₹{totalPaid}</h2>
          <p>Status: {getStatus()}</p>
        </div>

        <div className="p-4 bg-white shadow rounded">
          <p>Balance</p>
          <h2 className="text-xl font-bold text-red-600">₹{balance}</h2>
        </div>
      </div>

      {/* FORM */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          + Add Payment
        </button>
        <button
            onClick={() => navigate(`/tax-invoices/${id}`)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700 transition-colors flex items-center gap-2"
        >
          <span className="text-lg">📄</span>{" "}
          {/* Or use a Lucide icon like FileText */}
          View Tax Invoice
        </button>
      </div>

      {/* HISTORY */}
      <div className="bg-white p-4 md:p-6 rounded-xl shadow border">
        <h2 className="text-lg font-semibold mb-4">Payment History</h2>

        {history.length === 0 ? (
          <p className="text-gray-500">No payments found</p>
        ) : (
          <div className="overflow-x-auto">
            {/* DESKTOP TABLE */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left">
                    <th className="p-3">SlNo</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Mode</th>
                    <th className="p-3">Ref</th>
                    <th className="p-3">Download</th>
                  </tr>
                </thead>

                <tbody>
                  {history.map((p, index) => (
                    <tr key={p.id} className="border-b">
                      <td className="p-3">{index + 1}</td>
                      <td className="p-3">₹{p.amount}</td>
                      <td className="p-3">{p.payment_date}</td>
                      <td className="p-3">{p.mode}</td>
                      <td className="p-3">{p.txn_ref_no || "-"}</td>
                      <td className="p-3">
                        <button
                          onClick={() => handleDownload(p.id)}
                          className="text-blue-600"
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="md:hidden space-y-3">
        {history.map((p) => (
          <div key={p.id} className="bg-white border rounded p-3 shadow-sm">
            <div className="flex justify-between">
              <span className="font-semibold">₹{p.amount}</span>
              <span className="text-sm text-gray-500">{p.mode}</span>
            </div>

            <p className="text-sm text-gray-600">Date: {p.payment_date}</p>

            <p className="text-sm text-gray-600">Ref: {p.txn_ref_no || "-"}</p>

            <button
              onClick={() => handleDownload(p.id)}
              className="text-blue-600 text-sm mt-2"
            >
              Download File
            </button>
          </div>
        ))}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-lg p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Create Payment</h2>

              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="grid gap-3">
              <input
                type="number"
                name="amount"
                placeholder={`Max ₹${balance}`}
                value={form.amount}
                onChange={handleChange}
                className="border p-2 rounded"
                required
              />

              <input
                type="date"
                name="payment_date"
                value={form.payment_date}
                onChange={handleChange}
                className="border p-2 rounded"
                required
              />

              <select
                name="mode"
                value={form.mode}
                onChange={handleChange}
                className="border p-2 rounded"
              >
                <option value="upi">UPI</option>
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
                <option value="neft">NEFT</option>
                <option value="rtgs">RTGS</option>
                <option value="cheque">Cheque</option>
              </select>

              <input
                type="text"
                name="txn_ref_no"
                placeholder="Transaction Ref"
                value={form.txn_ref_no}
                onChange={handleChange}
                className="border p-2 rounded"
              />

              <input
                type="file"
                onChange={handleFile}
                className="border p-2 rounded"
              />

              {preview && (
                <a
                  href={preview}
                  target="_blank"
                  className="text-blue-600 text-sm"
                >
                  Preview File
                </a>
              )}

              <button
                type="submit"
                disabled={loading}
                className="bg-green-600 text-white py-2 rounded"
              >
                {loading ? "Saving..." : "Submit Payment"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
